// SP-2 monthly-cycle operability — HTTP transport for the staff statement
// lifecycle (generate / review / send to TPS / regenerate, the B3 queues) and
// the per-loan Monthly Cycle Cockpit read model. Statement writes are gated
// Statements.Generate (B3 + ADMIN); reads are Loans.Read. Hooks live in
// @/hooks/portfolioAdmin (useLoanStatements, useMonthlyCycle); query keys in
// loanStatementsKeys / monthlyCycleKeys.

import { api, API_BASE } from "@/api/client";

// --- Vocabulary --------------------------------------------------------------

// LoanReport.StatementStatus ladder. PendingReview (SP-2) = generated, awaiting
// the B3 review gate before it is sent to the TPS.
export type LoanStatementStatus =
  | "Draft"
  | "PendingReview"
  | "TpsApprovalPending"
  | "TpsApproved"
  | "Posted"
  | "Returned"
  | "Voided"
  | "Paid";

// Plain-language labels — no raw enum strings reach the user.
export const STATEMENT_STATUS_LABEL: Record<LoanStatementStatus, string> = {
  Draft: "Draft",
  PendingReview: "Waiting for your review",
  TpsApprovalPending: "With the servicer for review",
  TpsApproved: "Approved by the servicer",
  Posted: "Posted to the borrower",
  Returned: "Returned for correction",
  Voided: "Voided",
  Paid: "Paid",
};

// --- Statement shapes (mirror LoanStatementDto) -------------------------------

export interface LoanStatementLineItemsDto {
  principal: number;
  interest: number;
  servicingFee: number;
  fees: Array<{ feeEntryId: string; label: string; amount: number }> | null;
}

export interface LoanStatementDto {
  id: string;
  loanId: string;
  loanNumber: string;
  tpsId: string | null;
  tpsName: string | null;
  periodStart: string;
  periodEnd: string;
  statementStatus: LoanStatementStatus;
  amountDue: number;
  lineItems: LoanStatementLineItemsDto | null;
  preparedByUserName: string | null;
  preparedAt: string;
  tpsApprovedAt: string | null;
  postedAt: string | null;
  returnedReason: string | null;
  filedDocumentId: string | null;
  supersedesId: string | null;
  /** D-9 review gate — when/who B3 review-approved the PendingReview bill.
   *  Null on pre-gate rows and until the approve action runs. */
  reviewApprovedAt: string | null;
  reviewApprovedByUserName: string | null;
}

export interface GenerateLoanStatementRequest {
  periodStart: string; // yyyy-MM-dd
  periodEnd: string; // yyyy-MM-dd
}

// --- Review screen shapes (WS3/WS4/WS5 — mirror StatementReviewDto) -----------

export interface StatementReviewLineDto {
  key: string;
  label: string;
  statementAmount: number;
  computedAmount: number | null;
  adjustedAmount: number | null;
  adjustmentReason: string | null;
  adjustable: boolean;
  source: string;
}

export interface StatementAdjustmentDto {
  id: string;
  lineKey: string;
  calculatedAmount: number;
  adjustedAmount: number;
  reason: string;
  adjustedAt: string;
  adjustedByUserName: string | null;
}

export interface StatementCommentDto {
  id: string;
  authorRole: "phoenix" | "servicer";
  authorName: string | null;
  body: string;
  createdAt: string;
}

export interface StatementReviewDto {
  statementId: string;
  loanId: string;
  loanNumber: string;
  tpsName: string | null;
  periodStart: string;
  periodEnd: string;
  statementStatus: LoanStatementStatus;
  amountDue: number;
  reviewApprovedAt: string | null;
  reviewApprovedByUserName: string | null;
  returnedReason: string | null;
  ledgerExpectedAmount: number | null;
  computedAmountDue: number | null;
  regeneratedAmountDue: number | null;
  hasUnappliedChanges: boolean;
  isCurrentForPeriod: boolean;
  canAdjust: boolean;
  lines: StatementReviewLineDto[];
  adjustments: StatementAdjustmentDto[];
  comments: StatementCommentDto[];
}

// --- Monthly Cycle Cockpit shapes (mirror MonthlyCycleDtos.cs) ----------------

export type MonthlyCycleStepKey =
  | "statement"
  | "phoenix-review"
  | "tps-review"
  | "borrower-pays"
  | "payment-recorded"
  | "payout"
  | "complete";

export type MonthlyCycleStepState = "done" | "active" | "blocked" | "upcoming";

export interface MonthlyCycleStepDto {
  key: MonthlyCycleStepKey;
  state: MonthlyCycleStepState;
  at: string | null;
}

export interface MonthlyCycleStatementDto {
  id: string;
  status: LoanStatementStatus;
  amountDue: number;
  preparedAt: string;
  postedAt: string | null;
  returnedReason: string | null;
}

export interface MonthlyCycleStatementPaymentDto {
  id: string;
  status: "Initiated" | "Processing" | "Cleared" | "Returned" | "Canceled";
  method: string | null;
  amount: number;
  processingAt: string | null;
  clearedAt: string | null;
  returnedAt: string | null;
  failureReason: string | null;
}

export interface MonthlyCycleServicingPaymentDto {
  paymentId: string;
  amount: number;
  paymentDate: string;
  isReversed: boolean;
  /**
   * Distribution Gate (B3 payout gate) — the recorded run is held for B3
   * review; its allocation is not yet released for payout. The cockpit's
   * "payment recorded" step stays in progress while this is true. Optional so
   * existing inline test fixtures keep compiling (the LoanSummaryDto precedent).
   */
  pendingReview?: boolean;
}

export interface MonthlyCycleDistributionDto {
  id: string;
  status: string;
  totalAmount: number;
  approvedAt: string | null;
  blockReason: string | null;
}

export interface MonthlyCycleRecordPrefillDto {
  amount: number;
  billAmountDue: number;
  periodStart: string;
  /** The locked tps:intent:<pi> dedup key — submitted verbatim, never edited. */
  externalReference: string;
  payerName: string | null;
}

export interface MonthlyCycleReversalPrefillDto {
  servicingPaymentId: string;
  suggestedReason: string;
}

export interface MonthlyCycleDto {
  period: string; // "yyyy-MM"
  periodStart: string;
  periodEnd: string;
  dueDate: string | null;
  expectedAmount: number | null;
  tpsName: string | null;
  tpsReady: boolean;
  /** SP-3 — true when the loan sits on a REAL (non-default) TPS that hasn't
   *  finished its onboarding checklist. Optional so existing fixtures keep
   *  compiling; the wire payload always carries the field. */
  tpsSetupIncomplete?: boolean;
  availablePeriods: string[];
  statement: MonthlyCycleStatementDto | null;
  payment: MonthlyCycleStatementPaymentDto | null;
  servicingPayment: MonthlyCycleServicingPaymentDto | null;
  distribution: MonthlyCycleDistributionDto | null;
  steps: MonthlyCycleStepDto[];
  recordPrefill: MonthlyCycleRecordPrefillDto | null;
  reversalPrefill: MonthlyCycleReversalPrefillDto | null;
  /** True when this period's payment was recorded 100%-to-Phoenix by
   *  first-payment recording (period 0, outside the waterfall) rather than a
   *  tps:intent servicing run. The cockpit then shows the period as recorded and
   *  advances to distribution — no "Record payment" is offered. Optional so
   *  existing fixtures keep compiling; the wire payload always carries it. */
  recordedViaFirstPayment?: boolean;
}

// --- Transport ---------------------------------------------------------------

export const loanStatementsApi = {
  forLoan: (loanId: string) =>
    api.get<LoanStatementDto[]>(`/portfolio-admin/loans/${loanId}/statements`),
  get: (statementId: string) =>
    api.get<LoanStatementDto>(`/portfolio-admin/loan-statements/${statementId}`),
  generate: (loanId: string, body: GenerateLoanStatementRequest) =>
    api.post<LoanStatementDto>(
      `/portfolio-admin/loans/${loanId}/statements/generate`,
      body,
    ),
  regenerate: (statementId: string) =>
    api.post<LoanStatementDto>(
      `/portfolio-admin/loan-statements/${statementId}/regenerate`,
    ),
  sendToTps: (statementId: string) =>
    api.post<LoanStatementDto>(
      `/portfolio-admin/loan-statements/${statementId}/send-to-tps`,
    ),
  /** D-9 — B3's explicit review approval (stamps ReviewApprovedAt; unlocks send). */
  approveReview: (statementId: string) =>
    api.post<LoanStatementDto>(
      `/portfolio-admin/loan-statements/${statementId}/approve-review`,
    ),
  /** WS3 — the deterministic review payload (lines + provenance + delta + thread). */
  review: (statementId: string) =>
    api.get<StatementReviewDto>(
      `/portfolio-admin/loan-statements/${statementId}/review`,
    ),
  /** WS4 — create/replace one core line's binding adjustment (reason required). */
  upsertAdjustment: (
    statementId: string,
    lineKey: string,
    body: { adjustedAmount: number; reason: string },
  ) =>
    api.put<StatementAdjustmentDto>(
      `/portfolio-admin/loan-statements/${statementId}/adjustments/${lineKey}`,
      body,
    ),
  /** WS4 — remove one line's adjustment (reason required). */
  removeAdjustment: (statementId: string, lineKey: string, body: { reason: string }) =>
    api.post<unknown>(
      `/portfolio-admin/loan-statements/${statementId}/adjustments/${lineKey}/remove`,
      body,
    ),
  /** WS5 — B3's reply on the return thread. */
  addComment: (statementId: string, body: { body: string }) =>
    api.post<StatementCommentDto>(
      `/portfolio-admin/loan-statements/${statementId}/comments`,
      body,
    ),
  pendingReview: () =>
    api.get<LoanStatementDto[]>(`/portfolio-admin/loan-statements/pending-review`),
  correctionQueue: () =>
    api.get<LoanStatementDto[]>(`/portfolio-admin/loan-statements/correction-queue`),
  monthlyCycle: (loanId: string, period?: string) =>
    api.get<MonthlyCycleDto>(
      `/portfolio-admin/loans/${loanId}/monthly-cycle`,
      period ? { period } : undefined,
    ),
  /** Raw PDF endpoint (not enveloped) — used as a ReportPdfFrame src. Renders
   *  the SAME statement artifact the servicer approves and the borrower sees;
   *  gated Loans.Read (which every staff member on this surface already holds). */
  previewUrl: (statementId: string) =>
    `${API_BASE}/portfolio-admin/loan-statements/${statementId}/preview`,
};
